import * as React from 'react';
import { connect } from 'react-redux';
import { ApplicationState } from '../../../store';
import { SftpImportConfigurationWorkflow, ImportType, InventoryMode } from '../../../store/Models/Api/SftpImportModels';
import { ImportLibrarySettingsModal } from '../components/ImportLibrarySettingsModal';
import { ToxnotComponent, ToxnotComponentProperties } from '../../core/BaseApplicationComponents/ToxnotComponent';
import { ProductIdentificationSection } from '../sections/ProductIdentificationSection';
import { FileStructureSection } from '../sections/FileStructureSection';
import { AdvancedOptionsSection } from '../sections/AdvancedOptionsSection';
import { StepNavigationButtons } from '../components/StepNavigationButtons';

interface DefaultHandlingStepLocalProps {
    goToNextStep: () => void;
    goToPrevStep: () => void;    
    configData: SftpImportConfigurationWorkflow;
    onConfigChange: (data: Partial<SftpImportConfigurationWorkflow>) => void;
}

interface DefaultHandlingStepState {
    showModal: boolean;
}

type DefaultHandlingStepProps = DefaultHandlingStepLocalProps & ToxnotComponentProperties;

class DefaultHandlingStepComponent extends ToxnotComponent<DefaultHandlingStepProps, DefaultHandlingStepState> {
    constructor(props: DefaultHandlingStepProps) {
        super(props);
        this.state = {
            showModal: false
        };
    }

    public showModal = () => {
        // Validate header/footer inputs before proceeding
        const { configData } = this.props;
        const isValid = this.isStepValid();
        
        if (!isValid) {
            return; // Don't proceed if validation fails
        }
        
        if (configData.inventoryMode === 1) { // 1 = Multiple
            this.setState({ showModal: true });
        } else {
            this.props.goToNextStep();
        }
    }

    public isStepValid = () => {
        const { configData } = this.props;
        const isSupplierImport = configData.importType === ImportType.Suppliers;
        const isSingleImport = configData.inventoryMode === InventoryMode.One && !isSupplierImport;
        
        // Check if name is required for single product/material spreadsheet imports
        if (isSingleImport && (!configData.name || configData.name.trim() === "")) {
            return false;
        }
        
        // Check if header is enabled but headerEndRow is invalid (must be >= 0)
        if (configData.hasHeader && (configData.headerEndRow === undefined || configData.headerEndRow <= 0)) {
            return false;
        }
        
        // Check if footer is enabled but footerStartRow is invalid (must be > 0)
        if (configData.hasFooter && (configData.footerStartRow === undefined || configData.footerStartRow <= 0)) {
            return false;
        }
        
        return true;
    }

    public hideModal = () => {
        this.setState({ showModal: false });
    }

    public handleConfirm = () => {
        this.hideModal();
        this.props.goToNextStep();
    }

    render() {
        const { configData, onConfigChange } = this.props;
        const { showModal } = this.state;
        const isSupplierImport = configData.importType === ImportType.Suppliers;
        const isSingleImport = configData.inventoryMode === InventoryMode.One && !isSupplierImport;

        return (
            <div>
                <div className="tn-section-row">
                    <div className={isSupplierImport ? "tn-section-col-12" : "tn-section-col-6"}>
                        {isSingleImport && (
                            <ProductIdentificationSection
                                configData={configData}
                                onConfigChange={onConfigChange}
                                readOnly={false}
                                showValidation={true}
                            />
                        )}

                        <div className="start-subcontent-heading">
                            {this.localizeString("Select Options")}
                        </div>
                        <FileStructureSection
                            configData={configData}
                            onConfigChange={onConfigChange}
                            readOnly={false}
                        />
                    </div>
                    {!isSupplierImport && (
                        <div className="tn-section-col-6">
                            <AdvancedOptionsSection
                                configData={configData}
                                onConfigChange={onConfigChange}
                                readOnly={false}
                                collapsible={true}
                                defaultExpanded={true}
                            />
                        </div>
                    )}
                </div>
                <div className="tn-section-row">
                    <div className="tn-section-col-12">
                        <StepNavigationButtons
                            onPrevious={this.props.goToPrevStep}
                            onNext={this.showModal}
                            nextDisabled={!this.isStepValid()}
                            previousDisabled={!this.isStepValid()}
                        />
                    </div>
                </div>

                <ImportLibrarySettingsModal
                    show={showModal}
                    onClose={this.hideModal}
                    onConfirm={this.handleConfirm}
                    configData={configData}
                    onConfigChange={onConfigChange}
                />
            </div>
        );
    }
}

export const DefaultHandlingStep = connect(
    (state: ApplicationState) => ({ appServices: state.appServices! })
)(DefaultHandlingStepComponent);